import React, { useEffect, useMemo, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import DonutChart from '../../components/Charts/DonutChart'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthList } from '../../utils/coreUtils'
import {
    createChartExportHandlers,
    createMenuToggle,
    setupMenuClickOutside,
    exportToCSV,
} from '../../utils/exportUtils'
import './HuddlePerformanceByRole.css'

interface ZohoResponse {
    response?: {
        result?: {
            column_order?: string[]
            rows?: any[][]
        }
    }
}

interface AggregatedData {
    yes: number
    no: number
    total: number
}

interface HospitalPerformance {
    hospitalId: string
    hospitalName: string
    aggregated: AggregatedData
}

const SECTION_MENU_ID = 'hdl-performance-role-export-menu'
const Q13_MENU_ID = 'hdl-performance-role-q13-export-menu'
const HDL_SLIDER_KEY = 'hdl_slider'
const QUESTION = 'Q12: Was everything written on the board in patient friendly language?'
const Q13_QUESTION = 'Q13: Who wrote on the shared planning board during this huddle? Select all that apply.'

const Q13_ROLE_ORDER = ['Nurse', 'Mom/Birthing Person', 'Other', 'Provider', 'Support Person']
const Q13_COLORS = ['#0074B7', '#AEDCD5', '#B3B3B3', '#E48E43', '#429C75']

const getSliderIndices = (monthList: MonthInfo[], range: [number, number]): [number, number] => {
    if (!monthList.length) return [0, 0]
    const maxIndex = monthList.length - 1
    const [startVal, endVal] = range
    if (!startVal || !endVal) return [0, maxIndex]

    let startIdx = monthList.findIndex(m => m.value === startVal)
    if (startIdx === -1) startIdx = monthList.findIndex(m => m.value >= startVal)
    if (startIdx === -1) startIdx = 0

    let endIdx = -1
    for (let i = maxIndex; i >= 0; i--) {
        if (monthList[i].value <= endVal) { endIdx = i; break }
    }
    if (endIdx === -1) endIdx = maxIndex

    startIdx = Math.max(0, Math.min(startIdx, maxIndex))
    endIdx = Math.max(0, Math.min(endIdx, maxIndex))
    if (startIdx > endIdx) return [0, maxIndex]
    return [startIdx, endIdx]
}

const HuddlePerformanceByRole: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const hospitalIds = useMemo(
        () => (Array.isArray(selectedHospitalIds) ? selectedHospitalIds : []),
        [selectedHospitalIds]
    )

    const { data: performanceData, loading, initialLoading, error } = useHospitalData<ZohoResponse>(
        '/api/hdl/huddle_performance_role',
        hospitalIds
    )

    const { data: q13Data, loading: q13Loading, initialLoading: q13InitialLoading } = useHospitalData<ZohoResponse>(
        '/api/hdl/performance_role_engagement_q13',
        hospitalIds
    )

    const sectionRef = useRef<HTMLDivElement>(null)
    const q13Ref = useRef<HTMLDivElement>(null)
    const sectionExports = useMemo(
        () => createChartExportHandlers(
            sectionRef,
            'Huddle Performance By Role',
            'huddle_performance_by_role_q12',
            { fontBoost: true, exportTitle: 'Huddle Performance By Role', exportTitleAlign: 'center', exportTitleFontSize: '28px' }
        ),
        []
    )
    const q13Exports = useMemo(
        () => createChartExportHandlers(
            q13Ref,
            'Huddle Performance By Role – Engagement',
            'huddle_performance_role_engagement_q13',
            { fontBoost: true, exportTitle: 'Huddle Performance By Role – Engagement', exportTitleAlign: 'center', exportTitleFontSize: '28px' }
        ),
        []
    )
    const toggleSectionMenu = useMemo(() => createMenuToggle(SECTION_MENU_ID), [])
    const toggleQ13Menu = useMemo(() => createMenuToggle(Q13_MENU_ID), [])

    useEffect(() => {
        return setupMenuClickOutside(() => {
            sectionExports.closeMenu(SECTION_MENU_ID)
            q13Exports.closeMenu(Q13_MENU_ID)
        })
    }, [sectionExports, q13Exports])

    const rows: any[][] = useMemo(
        () => performanceData?.response?.result?.rows || [],
        [performanceData]
    )

    const colIdx = useMemo(() => {
        const columnOrder: string[] = performanceData?.response?.result?.column_order || []
        const find = (...keys: string[]) => {
            for (const key of keys) {
                const i = columnOrder.findIndex(c => c.toLowerCase() === key.toLowerCase())
                if (i !== -1) return i
            }
            return -1
        }
        const idx = (keys: string[], fallback: number) => {
            const found = find(...keys)
            return found !== -1 ? found : fallback
        }
        return {
            hospId:     idx(['hospital_id', 'hospitalid'], 0),
            hospName:   idx(['hospital_name', 'hospitalname'], 1),
            month:      idx(['month', 'month_year', 'date'], 2),
            yesCount:   idx(['yes_count', 'yes'], 3),
            noCount:    idx(['no_count', 'no'], 4),
            grandTotal: idx(['grand_total_count', 'grand_total', 'total'], 5),
        }
    }, [performanceData])

    const monthList: MonthInfo[] = useMemo(() => {
        if (!rows.length) return []
        return generateMonthList(rows, colIdx.month)
    }, [rows, colIdx])

    const hospitalIdsKey = hospitalIds.join('_')
    const persistenceKey = `${HDL_SLIDER_KEY}_${hospitalIdsKey}`
    const [sliderRangeValues, setSliderRangeValues] = usePersistedState<[number, number]>(
        persistenceKey,
        [0, 0]
    )

    const userInteractedRef = useRef(false)
    const initSeenRef = useRef(false)
    const lastDetectedKeyRef = useRef<string | null>(null)

    if (hospitalIds.length > 0 && lastDetectedKeyRef.current !== persistenceKey) {
        const isFirstInit = !initSeenRef.current
        lastDetectedKeyRef.current = persistenceKey
        initSeenRef.current = true

        if (isFirstInit) {
            try {
                const item = sessionStorage.getItem(persistenceKey)
                const parsed = item !== null ? JSON.parse(item) : null
                userInteractedRef.current =
                    Array.isArray(parsed) && parsed[0] !== 0 && parsed[1] !== 0
            } catch {
                userInteractedRef.current = false
            }
        } else {
            userInteractedRef.current = false
        }
    }

    useEffect(() => {
        if (!monthList.length) return
        if (loading) return
        const maxIdx = monthList.length - 1
        const fullRange: [number, number] = [monthList[0].value, monthList[maxIdx].value]
        const [curStart, curEnd] = sliderRangeValues

        if (!userInteractedRef.current) {
            if (curStart !== fullRange[0] || curEnd !== fullRange[1]) {
                setSliderRangeValues(fullRange)
            }
            return
        }

        const stillValid =
            curStart >= monthList[0].value &&
            curEnd <= monthList[maxIdx].value &&
            curStart <= curEnd
        if (!curStart || !curEnd || !stillValid) {
            userInteractedRef.current = false
            setSliderRangeValues(fullRange)
        }
    }, [hospitalIdsKey, monthList, loading, sliderRangeValues, setSliderRangeValues])

    const [startIdx, endIdx] = useMemo(
        () => getSliderIndices(monthList, sliderRangeValues),
        [monthList, sliderRangeValues]
    )

    const filteredRows = useMemo(() => {
        if (!monthList.length) return []
        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0
        return rows.filter(r => {
            const monthVal = parseMonthToValue(r[colIdx.month])
            return monthVal && monthVal >= minVal && monthVal <= maxVal
        })
    }, [rows, monthList, startIdx, endIdx, colIdx])

    const hospitalPerformanceData = useMemo(() => {
        const map = new Map<string, { yes: number; no: number; total: number; name: string }>()

        filteredRows.forEach(row => {
            const hospId = String(row[colIdx.hospId] || '')
            const hospName = String(row[colIdx.hospName] || '')
            const yes = Number(row[colIdx.yesCount]) || 0
            const no  = Number(row[colIdx.noCount]) || 0
            const total = Number(row[colIdx.grandTotal]) || 0

            if (!hospId) return

            if (!map.has(hospId)) {
                map.set(hospId, { yes: 0, no: 0, total: 0, name: hospName })
            }

            const data = map.get(hospId)!
            data.yes   += yes
            data.no    += no
            data.total += total
        })

        if (isMultiSelect && map.size > 1) {
            let pooledYes = 0
            let pooledNo = 0
            let pooledTotal = 0
            map.forEach(d => { pooledYes += d.yes; pooledNo += d.no; pooledTotal += d.total })
            return [{
                hospitalId: 'pooled',
                hospitalName: 'All Selected Hospitals',
                aggregated: { yes: pooledYes, no: pooledNo, total: pooledTotal },
            }]
        }

        const result: HospitalPerformance[] = Array.from(map.entries()).map(([hospId, data]) => ({
            hospitalId: hospId,
            hospitalName: data.name,
            aggregated: { yes: data.yes, no: data.no, total: data.total },
        }))

        return result.sort((a, b) => a.hospitalId.localeCompare(b.hospitalId))
    }, [filteredRows, isMultiSelect, colIdx])

    const handleDownloadCSV = () => {
        const startLabel = monthList[startIdx]?.label || ''
        const endLabel = monthList[endIdx]?.label || ''
        const monthRange = startLabel && endLabel ? `${startLabel} - ${endLabel}` : ''

        const csvRows: (string | number)[][] = []
        const hospitalIdLabel = hospitalIds.join(', ')

        hospitalPerformanceData.forEach(hospital => {
            const hospId = hospital.hospitalId === 'pooled'
                ? hospitalIdLabel
                : hospital.hospitalId
            csvRows.push([hospId, monthRange, QUESTION, 'Yes', hospital.aggregated.yes])
            csvRows.push([hospId, monthRange, QUESTION, 'No', hospital.aggregated.no])
            csvRows.push([hospId, monthRange, QUESTION, 'Grand Total', hospital.aggregated.total])
        })

        exportToCSV({
            headers: ['Hospital Id', 'Date Range', 'Question', 'Answer', 'Count'],
            rows: csvRows,
            filename: 'huddle_performance_by_role_q12',
        })
    }

    const q13Rows: any[][] = useMemo(
        () => q13Data?.response?.result?.rows || [],
        [q13Data]
    )

    const q13ColIdx = useMemo(() => {
        const columnOrder: string[] = q13Data?.response?.result?.column_order || []
        const find = (...keys: string[]) => {
            for (const key of keys) {
                const i = columnOrder.findIndex(c => c.toLowerCase() === key.toLowerCase())
                if (i !== -1) return i
            }
            return -1
        }
        return {
            month:     find('month', 'month_year', 'date')                !== -1 ? find('month', 'month_year', 'date')                : 2,
            role:      find('role', 'role_type', 'category', 'who_wrote') !== -1 ? find('role', 'role_type', 'category', 'who_wrote') : 3,
            numerator: find('numerator', 'count', 'role_count')           !== -1 ? find('numerator', 'count', 'role_count')           : 4,
        }
    }, [q13Data])

    const q13Series = useMemo(() => {
        const counts: Record<string, number> = {}

        if (!monthList.length || !q13Rows.length) return Q13_ROLE_ORDER.map(() => 0)

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        q13Rows.forEach(r => {
            const monthVal = parseMonthToValue(r[q13ColIdx.month])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const role = String(r[q13ColIdx.role] || '').trim().toLowerCase()
            const numerator = Number(r[q13ColIdx.numerator]) || 0
            if (!role) return
            counts[role] = (counts[role] || 0) + numerator
        })

        return Q13_ROLE_ORDER.map(role => counts[role.toLowerCase()] || 0)
    }, [q13Rows, q13ColIdx, monthList, startIdx, endIdx])

    const q13IsRefreshing = !q13InitialLoading && q13Loading
    const q13HasData = q13Series.some(v => v > 0)

    const handleDownloadQ13CSV = () => {
        const startLabel = monthList[startIdx]?.label || ''
        const endLabel = monthList[endIdx]?.label || ''
        const monthRange = startLabel && endLabel ? `${startLabel} - ${endLabel}` : ''
        const hospitalIdLabel = hospitalIds.join(', ')
        const total = q13Series.reduce((s, v) => s + v, 0)

        // Largest remainder rounding — matches DonutChart label calculation exactly
        const rawPcts = q13Series.map(c => total > 0 ? (c / total) * 100 : 0)
        const floored = rawPcts.map(p => Math.floor(p))
        const remainders = rawPcts.map((p, i) => ({ i, r: p - floored[i] })).sort((a, b) => b.r - a.r)
        let deficit = 100 - floored.reduce((s, v) => s + v, 0)
        const adjusted = [...floored]
        for (const { i } of remainders) {
            if (deficit <= 0) break
            adjusted[i]++
            deficit--
        }

        const csvRows: (string | number)[][] = Q13_ROLE_ORDER.map((role, i) => [
            hospitalIdLabel, monthRange, Q13_QUESTION, role, q13Series[i], `${adjusted[i]}%`
        ])
        exportToCSV({
            headers: ['Hospital Id', 'Date Range', 'Question', 'Role', 'Count', 'Percentage'],
            rows: csvRows,
            filename: 'huddle_performance_role_engagement_q13',
        })
    }

    const showInitialLoading = initialLoading && loading
    const isRefreshing = !initialLoading && loading
    const hasHospitals = hospitalIds.length > 0
    const noData = !initialLoading && !loading && !monthList.length

    const stableDataRef = useRef<HospitalPerformance[]>([])
    const confirmedKeyRef = useRef<string>('')
    const wasLoadingRef = useRef(false)

    const loadingJustFinished = wasLoadingRef.current && !loading
    wasLoadingRef.current = loading

    if (loadingJustFinished) {
        if (hospitalPerformanceData.length > 0) {
            stableDataRef.current = hospitalPerformanceData
        }
        confirmedKeyRef.current = hospitalIdsKey
    }

    const displayedData = (isRefreshing || confirmedKeyRef.current !== hospitalIdsKey) && stableDataRef.current.length > 0
        ? stableDataRef.current
        : hospitalPerformanceData

    const hasData = displayedData.length > 0

    return (
        <div className="hdl-performance-role">
            <div className="hdl-page-header">
                <h1 className="hdl-page-title">Huddle Performance By Role</h1>
                <div className="hdl-slider-wrap">
                    {monthList.length > 0 && (
                        <DateRangeSlider
                            monthList={monthList}
                            startIndex={startIdx}
                            endIndex={endIdx}
                            onChange={(s, e) => {
                                const sv = monthList[s]?.value || 0
                                const ev = monthList[e]?.value || 0
                                if (sv && ev) {
                                    userInteractedRef.current = true
                                    setSliderRangeValues([sv, ev])
                                }
                            }}
                        />
                    )}
                </div>
            </div>

            <div className={`hdl-card${hasHospitals && !error && noData ? ' hdl-card-empty' : ''}`}>
                {showInitialLoading && <LoadingNotice title="Loading performance data…" />}

                {!hasHospitals && (
                    <div className="hdl-notice">Select at least one hospital to view data.</div>
                )}

                {hasHospitals && error && (
                    <div className="hdl-notice hdl-notice-error">Failed to load data: {error}</div>
                )}

                <NoDataOverlay visible={hasHospitals && !error && noData} />

                {hasHospitals && !error && !noData && (
                    <div className="hdl-performance-role-section" ref={sectionRef} style={{ marginTop: '1.5rem' }}>
                        <div className="hdl-performance-role-content">
                            {hasData ? (
                                <div className="hdl-datatable-wrapper">
                                    <div className="hdl-datatable-header">
                                        <span className="hdl-card-category">Accessibility</span>
                                        <span className="hdl-card-question">{QUESTION}</span>
                                        <div className="export-menu export-ignore">
                                            <button className="hamburger" onClick={toggleSectionMenu}>&#9776;</button>
                                            <div id={SECTION_MENU_ID} className="export-dropdown">
                                                <div onClick={() => sectionExports.handleDownloadSVG(SECTION_MENU_ID)}>Download SVG</div>
                                                <div onClick={() => sectionExports.handleDownloadPNG(SECTION_MENU_ID)}>Download PNG</div>
                                                <div onClick={() => { handleDownloadCSV(); sectionExports.closeMenu(SECTION_MENU_ID); }}>Download CSV</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="hdl-card-body">
                                        {displayedData.map((hospital, hospIdx) => (
                                            <React.Fragment key={hospIdx}>
                                                {displayedData.length > 1 && (
                                                    <div className="hdl-hospital-row-label">
                                                        {hospital.hospitalName || hospital.hospitalId}
                                                    </div>
                                                )}
                                                <div className="hdl-card-row hdl-card-row--yes">
                                                    <span className="hdl-card-label">Yes</span>
                                                    <span className="hdl-card-value">{hospital.aggregated.yes}</span>
                                                </div>
                                                <div className="hdl-card-row hdl-card-row--no">
                                                    <span className="hdl-card-label">No</span>
                                                    <span className="hdl-card-value">{hospital.aggregated.no}</span>
                                                </div>
                                                <div className="hdl-card-row hdl-card-row--total">
                                                    <span className="hdl-card-label">Grand Total</span>
                                                    <span className="hdl-card-value">{hospital.aggregated.total}</span>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="hdl-performance-role-empty">No data available for the selected date range.</div>
                            )}
                        </div>

                        <div className="hdl-q13-section" ref={q13Ref}>
                            <div className="hdl-datatable-header">
                                <span className="hdl-card-category hdl-card-category--engagement">Engagement</span>
                                <span className="hdl-card-question">{Q13_QUESTION}</span>
                                <div className="export-menu export-ignore">
                                    <button className="hamburger" onClick={toggleQ13Menu}>&#9776;</button>
                                    <div id={Q13_MENU_ID} className="export-dropdown">
                                        <div onClick={() => q13Exports.handleDownloadSVG(Q13_MENU_ID)}>Download SVG</div>
                                        <div onClick={() => q13Exports.handleDownloadPNG(Q13_MENU_ID)}>Download PNG</div>
                                        <div onClick={() => { handleDownloadQ13CSV(); q13Exports.closeMenu(Q13_MENU_ID) }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            <div className="hdl-q13-chart-wrap" style={{ position: 'relative' }}>
                                {q13HasData ? (
                                    <DonutChart
                                        labels={Q13_ROLE_ORDER}
                                        series={q13Series}
                                        tooltipCounts={q13Series}
                                        colors={Q13_COLORS}
                                        height={300}
                                        type="pie"
                                        legendPosition="right"
                                        showLegend={true}
                                        legendFontSize="14px"
                                        legendFontWeight="400"
                                        legendLineHeight={1.3}
                                        legendOffsetX={-20}
                                        legendOffsetY={30}
                                        dataLabelOffset={50}
                                        pieOffsetX={-30}
                                        pieOffsetY={30}
                                        dataLabelColor="#292929"
                                        dataLabelFontSize="14px"
                                        dataLabelFontWeight="400"
                                        dataLabelDropShadow={false}
                                        exportFilename="huddle_performance_role_engagement_q13"
                                    />
                                ) : (
                                    <div className="hdl-performance-role-empty">No data available for the selected date range.</div>
                                )}
                                {q13IsRefreshing && (
                                    <div className="chart-loading-overlay">
                                        <div>Loading...</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {isRefreshing && (
                            <div className="chart-loading-overlay">
                                <div>Loading...</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default HuddlePerformanceByRole
